import { format, parse, subYears } from 'date-fns';
import { useMemo } from 'react';
import { useQuery } from 'urql';

import { COMPOSITION_GET_DATA_V3, EntKind } from '@revelio/data-access';

import { isEmptyCompositionBucket } from '../../CustomDash/components/DataProvider/composition/composition-v3/is-empty-composition-bucket';
import { buildCompositionV3Variables } from '../../CustomDash/filters/utils/composition-v3/build-composition-v3-variables';
import { getMonthRefsFromCompositionV3Filters } from '../../CustomDash/filters/utils/composition-v3/month-refs-from-v3-filters';
import { useMonthApiFilters } from '../../entity-summary/use-month-api-filters';
import type { ResolvedProfileIds } from './useResolveProfileIds';

const TREND_LOOKBACK_YEARS = 3;

export interface RoleSkillTimepoint {
  id: number;
  date: string;
  count: number;
  share: number;
}

export interface RoleSkill {
  id: number;
  shortName: string;
  longName: string;
  latestShare: number;
  timeseries: RoleSkillTimepoint[];
}

export interface UseRoleSkillCompositionResult {
  roleSkills: RoleSkill[];
  isLoading: boolean;
  error: string | null;
}

export const useRoleSkillComposition = (
  resolvedIds: ResolvedProfileIds
): UseRoleSkillCompositionResult => {
  const dateFilters = useMonthApiFilters();

  const variables = useMemo(() => {
    if (!resolvedIds.roleId || !dateFilters) {
      return buildCompositionV3Variables({ entities: [] });
    }

    const endDate = dateFilters.date_range_api_filters.end_date;
    const parsedEnd = parse(endDate, 'yyyy-MM', new Date());
    const startDate = format(
      subYears(parsedEnd, TREND_LOOKBACK_YEARS),
      'yyyy-MM'
    );

    return buildCompositionV3Variables({
      entities: [
        {
          EntID: resolvedIds.roleId,
          EntType: EntKind.RoleLvl_3,
        },
      ],
      filters: {
        start_date: startDate,
        end_date: endDate,
        ...(resolvedIds.seniorityId
          ? { seniority: [resolvedIds.seniorityId] }
          : {}),
      },
    });
  }, [resolvedIds.roleId, resolvedIds.seniorityId, dateFilters]);

  const [{ data, fetching, error }] = useQuery({
    query: COMPOSITION_GET_DATA_V3,
    variables,
    pause: !resolvedIds.roleId || !dateFilters,
  });

  const roleSkills = useMemo((): RoleSkill[] => {
    const skills = data?.composition_v3?.entities?.[0]?.metrics?.skills;
    if (!skills) return [];

    const monthRefs = getMonthRefsFromCompositionV3Filters(variables.filters);

    return skills
      .filter((skill) => !isEmptyCompositionBucket(skill.id))
      .map((skill) => {
        const ts: RoleSkillTimepoint[] = skill.headcount.map((t) => ({
          id: t.id,
          date: monthRefs[t.id] ?? '',
          count: t.count,
          share: t.share,
        }));

        const latestShare = ts.length > 0 ? (ts[ts.length - 1]?.share ?? 0) : 0;

        return {
          id: skill.id,
          shortName: skill.short_name,
          longName: skill.long_name,
          latestShare,
          timeseries: ts,
        };
      })
      .sort((a, b) => b.latestShare - a.latestShare);
  }, [data, variables]);

  return {
    roleSkills,
    isLoading: fetching,
    error: error ? 'Failed to load role skill composition' : null,
  };
};
